import {
    BackendSchema,
    CheckboxOptions,
    createCheckboxOptions,
    daysOfWeek,
    hourRanges,
    Rule,
    RuleSchemaType,
    RuleType,
} from "layouts/charging-rules/interface/Rules"
import { createInitialRules } from "layouts/charging-rules/state/createRules.tsx"
import { IsActive } from "layouts/charging-rules/utils/utils.ts"

export type Action =
    | { type: "initFromSchema"; schema: RuleSchemaType; companyRule: BackendSchema }
    | { type: "toggleOption"; ruleType: RuleType; option: keyof CheckboxOptions }
    | { type: "updateValue"; ruleType: RuleType; value: number | null }
    | { type: "deactivate"; ruleType: RuleType }
    | { type: "activate"; ruleType: RuleType }
    | { type: "company"; ruleType: RuleType }
    | { type: "setActive"; ruleType: RuleType; isActive: IsActive }

export function rulesReducer(state: Rule[], action: Action): Rule[] {
    switch (action.type) {
        case "initFromSchema":
            return createInitialRules(action.schema, action.companyRule)
        case "toggleOption":
            return state.map(rule =>
                rule.type === action.ruleType && rule.kind === "checkbox" && rule.options
                    ?
                    {
                        ...rule,
                        options: {
                            ...rule.options,
                            [action.option]: !rule.options[action.option],
                        },
                    }
                    : rule
            )
        case "updateValue": {
            return state.map(rule =>
                rule.type === action.ruleType && rule.kind === "number"
                    ? {
                        ...rule,
                        value: action.value,
                    }
                    : rule
            )
        }

        case "deactivate":
            return state.map(rule => {
                if (rule.kind === "checkbox" && rule.type === action.ruleType) {
                    const keys = rule.type === RuleType.DOWBlocked ? daysOfWeek : hourRanges
                    const allFalse = Object.fromEntries(keys.map(k => [k, false]))
                    return {
                        ...rule,
                        options: allFalse,
                    }
                } else if (rule.kind === "number" && rule.type === action.ruleType) {
                    return { ...rule, value: null }
                } else {
                    return rule
                }
            })
        case "company":
            return state.map(rule => {
                if (rule.kind === "checkbox" && rule.type == action.ruleType) {
                    return {
                        ...rule,
                        options: createCheckboxOptions(action.ruleType === RuleType.DOWBlocked ? daysOfWeek : hourRanges, true)
                    };
                } else if (rule.kind === "number" && rule.type == action.ruleType) {
                    return {
                        ...rule,
                        value: 1,
                    };
                } else {
                    return rule
                }
            })

        case "activate":
            return state.map((rule) => {
                if (rule.type !== action.ruleType) return rule;

                if (rule.kind === "checkbox" && rule.type == action.ruleType) {
                    let restart: Record<string, boolean> = {};


                    if (rule.type === RuleType.DOWBlocked) {
                        restart = Object.fromEntries(daysOfWeek.map(day => [day, false]));
                        restart["sunday"] = true;
                    }
                    if (rule.type === RuleType.HoursBlocked) {
                        restart = Object.fromEntries(hourRanges.map(range => [range, false]));
                        restart["00h-02h"] = true;
                        restart["02h-04h"] = true;
                    }

                    return {
                        ...rule,
                        options: restart
                    };
                } else if (rule.kind === "number" && rule.type == action.ruleType) {
                    if (rule.type === RuleType.MaxRechargeEnergyOptions) {
                        return {
                            ...rule,
                            value: 100,
                        };
                    }
                    if (rule.type === RuleType.MaxRechargeDurationOptions) {
                        return {
                            ...rule,
                            value: 240,
                        };
                    }
                }
                return rule;
            });

        case "setActive":
            return state.map(rule =>
                rule.type === action.ruleType
                    ? { ...rule, isActive: action.isActive }
                    : rule
            );
        default:
            return state
    }
}
